import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoClose } from "react-icons/io5";
import { FaRegUserCircle } from "react-icons/fa";
import "./shareModal.css";
import { SET_IS_SHOW_SHARE } from "../../../../../../store/constants";
import { IMAGE_URL, API_URL } from "../../../../../../URL";
import {getCookie} from "../../../../../../hooks/getCookie";

const ROLE_MAP_FROM_BACK = {
    1: "write",
    2: "admin",
};

const ROLE_MAP_TO_BACK = {
    write: 1,
    admin: 2,
};

export const ShareModal = () => {
    const dispatch = useDispatch();
    const board = useSelector((state) => state.boards.activeBoard || {});

    const [searchValue, setSearchValue] = useState("");
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const closeModal = () => {
        dispatch({ type: SET_IS_SHOW_SHARE, payload: false });
    };

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains("shareModalOverlay")) {
            closeModal();
        }
    };

    const handleCopyLink = async () => {
        try {
            const shareLink = `${window.location.origin}/board/${board?.id || ""}`;
            await navigator.clipboard.writeText(shareLink);
        } catch (error) {
            console.error("Copy failed:", error);
        }
    };
    const isImage = board?.path?.startsWith("uploads");
    const previewImage = isImage
        ? `${IMAGE_URL}/media/${board.path}`
        : board.path;

    useEffect(() => {
        const normalizedRbac = (board?.rbac || []).map((item) => ({
            id: item.user_id,
            user_id: item.user_id,
            username: item.username || `User ${item.user_id}`,
            email: item.email || "",
            rbac_id: item.id,
            role: ROLE_MAP_FROM_BACK[item.role] || "write",
        }));

        setSelectedUsers(normalizedRbac);
    }, [board]);

    const selectedUserIds = useMemo(() => {
        return selectedUsers.map((user) => user.id);
    }, [selectedUsers]);

    useEffect(() => {
        const timeout = setTimeout(async () => {
            if (!searchValue.trim()) {
                setMembers([]);
                return;
            }

            try {
                setLoading(true);
                const csrfToken = getCookie("csrftoken");
                const res = await fetch(API_URL + "/post/search_members/", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        email: searchValue,
                    }),
                });

                const data = await res.json();

                if (res.ok) {
                    const users = Array.isArray(data) ? data : data ? [data] : [];

                    const filteredUsers = users.filter(
                        (user) => !selectedUserIds.includes(user.id)
                    );

                    setMembers(filteredUsers);
                } else {
                    setMembers([]);
                }
            } catch (error) {
                console.error("Search failed:", error);
                setMembers([]);
            } finally {
                setLoading(false);
            }
        }, 400);

        return () => clearTimeout(timeout);
    }, [searchValue, selectedUserIds]);

    const handleSelectUser = async (user) => {
        const isExist = selectedUsers.some((item) => item.id === user.id);
        if (isExist) return;

        const optimisticUser = {
            ...user,
            role: "write",
        };

        setSelectedUsers((prev) => [...prev, optimisticUser]);

        try {
            const csrfToken = getCookie("csrftoken");
            const res = await fetch(API_URL + "/post/set_board_role/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "include",
                body: JSON.stringify({
                    board_id: board?.id,
                    user_id: user?.id,
                    role: ROLE_MAP_TO_BACK.write,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok) {
                throw new Error(data?.detail || "Failed to add member");
            }

            setSelectedUsers((prev) =>
                prev.map((item) =>
                    item.id === user.id
                        ? {
                            ...item,
                            rbac_id: data?.id || item.rbac_id,
                        }
                        : item
                )
            );
        } catch (error) {
            console.error(error);
            setSelectedUsers((prev) => prev.filter((item) => item.id !== user.id));
        }

        setSearchValue("");
        setMembers([]);
    };

    const handleRemoveSelectedUser = async (id, rbac_id) => {
        const removedUser = selectedUsers.find((user) => user.id === id);

        setSelectedUsers((prev) => prev.filter((user) => user.id !== id));

        try {
            const csrfToken = getCookie("csrftoken");
            await fetch(API_URL + "/delete/remove_board_role/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "include",
                body: JSON.stringify({
                    id: rbac_id
                }),
            });
        } catch (error) {
            console.error("Remove failed:", error);

            if (removedUser) {
                setSelectedUsers((prev) => [...prev, removedUser]);
            }
        }
    };

    const handleChangeRole = async (id, role) => {
        const previousUsers = [...selectedUsers];

        setSelectedUsers((prev) =>
            prev.map((user) =>
                user.id === id ? { ...user, role } : user
            )
        );

        try {
            const csrfToken = getCookie("csrftoken");
            const res = await fetch(API_URL + "/post/change_board_role/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "include",
                body: JSON.stringify({
                    board_id: board?.id,
                    user_id: id,
                    role: ROLE_MAP_TO_BACK[role],
                }),
            });

            if (!res.ok) {
                throw new Error("Failed to change role");
            }
        } catch (error) {
            console.error(error);
            setSelectedUsers(previousUsers);
        }
    };

    return (
        <div className="shareModalOverlay" onClick={handleOverlayClick}>
            <div className="shareModal" onClick={(e) => e.stopPropagation()}>
                <div className="shareModal__header">
                    <h2>Share board</h2>
                    <button
                        type="button"
                        className="shareModal__close"
                        onClick={closeModal}
                    >
                        <IoClose />
                    </button>
                </div>

                <div className="shareModal__boardInfo">
                    <div className="shareModal__boardPreview">
                        {isImage ? (
                            <img
                                src={previewImage}
                                alt={board?.name || "Board"}
                                className="shareModal__boardImage"
                            />
                        ) : (
                            <div
                                className="shareModal__boardColor"
                                style={{
                                    background: previewImage || "#1d7afc"
                                }}
                            />
                        )}
                    </div>

                    <div className="shareModal__boardText">
                        <h3>{board?.name || "Board name"}</h3>
                        <p>Invite members and manage their roles for this board.</p>
                    </div>
                </div>

                <div className="shareModal__inviteWrapper">
                    <div className="shareModal__invite">
                        <input
                            type="text"
                            placeholder="Enter email"
                            className="shareModal__input"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    </div>

                    {searchValue.trim() && (
                        <div className="shareModal__searchDropdown">
                            {loading ? (
                                <div className="shareModal__searchItem">Loading...</div>
                            ) : members.length > 0 ? (
                                members.map((user) => (
                                    <div
                                        key={user.id}
                                        className="shareModal__searchItem"
                                        onClick={() => handleSelectUser(user)}
                                    >
                                        <div className="shareModal__searchAvatar">
                                            <FaRegUserCircle />
                                        </div>

                                        <div className="shareModal__searchText">
                                            <h4>{user.username || "User"}</h4>
                                            <p>{user.email}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="shareModal__searchItem">No members found</div>
                            )}
                        </div>
                    )}

                    {selectedUsers.length > 0 && (
                        <div className="shareModal__membersList">
                            {selectedUsers.map((user) => (
                                <div key={user.id} className="shareModal__memberRow">
                                    <div className="shareModal__memberInfo">
                                        <div className="shareModal__searchAvatar">
                                            <FaRegUserCircle />
                                        </div>

                                        <div className="shareModal__memberText">
                                            <h4>{user.email || `User ${user.id}`}</h4>
                                        </div>
                                    </div>

                                    <div className="shareModal__memberActions">
                                        <select
                                            className="shareModal__roleSelect"
                                            value={user.role}
                                            onChange={(e) =>
                                                handleChangeRole(user.id, e.target.value)
                                            }
                                        >
                                            <option value="write">Write</option>
                                            <option value="admin">Admin</option>
                                        </select>

                                        <button
                                            type="button"
                                            className="shareModal__removeUser"
                                            onClick={() => handleRemoveSelectedUser(user.id, user.rbac_id)}
                                        >
                                            <IoClose />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="shareModal__access">
                    <div className="shareModal__accessLeft">
                        <FaRegUserCircle className="shareModal__accessIcon" />
                        <div>
                            <h4>Board access</h4>
                            <p>Only invited members can access this board</p>
                        </div>
                    </div>

                    <select className="shareModal__select" defaultValue="Private">
                        <option>Private</option>
                        <option>Workspace</option>
                        <option>Public</option>
                    </select>
                </div>

                <div className="shareModal__footer">
                    <button
                        type="button"
                        className="shareModal__copyBtn"
                        onClick={handleCopyLink}
                    >
                        Copy board link
                    </button>
                </div>
            </div>
        </div>
    );
};